<template>
  <header class="header">
    <!-- 页面跳转过渡层 -->
    <div class="transition-overlay" :class="{ active: isPageLoading }">
      <div class="loader"></div>
    </div>

    <div class="header__container">
      <!-- 左侧 Logo -->
      <div class="logo" @click="goHome">YOLEE</div>

      <!-- 右侧 菜单区（桌面） -->
      <nav class="menu desktop-only">
        <ul>
          <!-- Home 单独渲染在最前 -->
          <li :class="{ active: isActive('/') }" @click="navigate('/')">
            Home
          </li>
          <li
            v-for="item in headData"
            :key="item.id"
            :class="{ active: isActive(item.id) }"
            @mouseenter="handleMouseEnter(item)"
            @mouseleave="handleMouseLeave"
            @click="handleMainNavClick"
          >
            <router-link
              :to="{
                name: 'MainCategory',
                params: {
                  mainCategoryId: item.id,
                },
              }"
              >{{ item.name }}</router-link
            >
          </li>
          <li
            v-for="(item, i) in staticNavItems.slice(1)"
            :key="item.path + i"
            :class="{ active: isActive(item.path) }"
            @click="navigate(item.path, true)"
          >
            {{ item.name }}
          </li>
        </ul>
      </nav>

      <!-- 汉堡按钮（移动端） -->
      <button class="mobile-menu-btn mobile-only" @click="toggleMobileMenu">
        <Icon :fontName="isMobileMenuOpen ? 'close' : 'hamburger'" />
      </button>
    </div>

    <!-- 移动端菜单 -->
    <transition name="fade">
      <div
        class="mobile-menu"
        v-if="isMobileMenuOpen"
        @click.self="toggleMobileMenu"
      >
        <ul>
          <!-- Home 单独渲染 -->
          <li @click="closeAllMenus('/')">Home</li>
          <li
            v-for="item in headData"
            :key="item.id"
            @click="closeAllMenus(`/categories/main/${item.id}`)"
          >
            {{ item.name }}
          </li>
          <!-- 其他静态导航 -->
          <li
            v-for="(item, i) in staticNavItems.slice(1)"
            :key="item.path + i"
            @click="closeAllMenus(item.path)"
          >
            {{ item.name }}
          </li>
        </ul>
      </div>
    </transition>

    <!-- 子分类和产品信息展示 -->
    <transition name="fade">
      <div
        class="submenu-overlay"
        v-if="showSubmenu"
        @mouseenter="clearHideTimer"
        @mouseleave="handleMouseLeave"
      >
        <div class="submenu-content">
          <div v-if="currentMainCategory">
            <h3>{{ currentMainCategory.name }}</h3>
            <div
              v-for="sub in currentMainCategory.subCategories
                .slice()
                .sort((a, b) => a.id - b.id)"
              :key="sub.id"
              class="submenu-section"
            >
              <h4 @click="closeAllMenus(`/categories/sub/${sub.id}`)">
                {{ sub.name }}
              </h4>
              <ul>
                <li
                  v-for="product in sub.products
                    .slice()
                    .sort((a, b) => a.order - b.order)"
                  :key="product.productId"
                  @click="closeAllMenus(`/product/${product.productId}`)"
                >
                  {{ product.name }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import Icon from "@/components/Icon";

export default {
  name: "Header",
  components: {
    Icon,
  },
  props: {
    headData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isMobileMenuOpen: false,
      staticNavItems: [
        { name: "Home", path: "/" }, // 新增
        { name: "About Us", path: "/about" },
        { name: "Contact", path: "/contact" },
      ],
      currentMainCategory: null,
      showSubmenu: false,
      hideTimer: null,
      showTimer: null,
      isPageLoading: false,
    };
  },
  methods: {
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      document.body.style.overflow = this.isMobileMenuOpen ? "hidden" : "";

      // 关闭桌面端子菜单
      if (this.isMobileMenuOpen) {
        this.showSubmenu = false;
        this.currentMainCategory = null;
      }
    },
    goHome() {
      this.closeAllMenus("/");
    },
    closeAllMenus(path) {
      // 如果目标路径和当前路径相同，且不是产品详情页，则不刷新
      if (path === this.$route.path && !path.startsWith("/product/")) {
        this.isMobileMenuOpen = false;
        document.body.style.overflow = "";
        this.showSubmenu = false;
        this.currentMainCategory = null;
        return;
      }

      this.isPageLoading = true;
      this.isMobileMenuOpen = false;
      document.body.style.overflow = "";
      this.showSubmenu = false;
      this.currentMainCategory = null;

      const handleNavigation = () => {
        if (path.startsWith("/categories/main")) {
          const mainCategoryId = path.split("/").pop();
          return this.$router.push({
            name: "MainCategory",
            params: { mainCategoryId },
          });
        } else if (path.startsWith("/categories/sub")) {
          const subCategoryId = path.split("/").pop();
          return this.$router.push({
            name: "SubCategory",
            params: { subCategoryId },
          });
        } else if (path.startsWith("/product/")) {
          const productId = path.split("/").pop();
          return this.$router
            .push({ name: "ProductDetail", params: { id: productId } })
            .catch((err) => {
              if (err && err.name === "NavigationDuplicated") {
                this.$router.go(0);
              }
            });
        } else {
          return this.$router.push(path);
        }
      };

      handleNavigation().finally(() => {
        this.isPageLoading = false;
      });
    },

    navigate(path, closeMobile = false) {
      this.closeAllMenus(path);
    },

    isActive(idOrPath) {
      // 如果传入的是路径（如 "/about"）
      if (typeof idOrPath === "string" && idOrPath.startsWith("/")) {
        return this.$route.path === idOrPath;
      }

      // 如果传入的是主分类ID
      if (
        this.$route.name === "MainCategory" &&
        parseInt(this.$route.params.mainCategoryId) === idOrPath
      ) {
        return true;
      }

      return false;
    },
    handleMainNavClick() {
      this.showSubmenu = false;
      this.currentMainCategory = null;
    },
    handleMouseEnter(item) {
      clearTimeout(this.hideTimer);
      clearTimeout(this.showTimer);

      this.showTimer = setTimeout(() => {
        this.currentMainCategory = item;
        this.showSubmenu = true;
      }, 300);
    },
    handleMouseLeave() {
      clearTimeout(this.showTimer);

      this.hideTimer = setTimeout(() => {
        this.showSubmenu = false;
        this.currentMainCategory = null;
      }, 300);
    },
    clearHideTimer() {
      clearTimeout(this.hideTimer);
    },
  },
  beforeUnmount() {
    clearTimeout(this.hideTimer);
    clearTimeout(this.showTimer);
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.header {
  position: relative;
  width: 100%;
  background-color: @color-bg-main;
  border-bottom: 1px solid @color-bg-light;
  padding: 1rem 2rem;

  .header__container {
    .flex-center(row);
    justify-content: space-between;

    .logo {
      font-size: 1.5rem;
      font-weight: bold;
      color: @color-primary;
      cursor: pointer;
    }

    .menu {
      ul {
        display: flex;
        gap: 2rem;
        li {
          position: relative;
          color: @color-text-content;
          cursor: pointer;
          .transition(color, 0.3s);

          &:hover {
            color: darken(@color-status-warn, 20%);
          }

          &::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: -5px;
            width: 100%;
            height: 2px;
            background-color: @color-active;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease;
          }

          &:hover::after,
          &.active::after {
            transform: scaleX(1);
          }

          &.active {
            color: @color-primary;
          }
        }
      }
    }

    .mobile-menu-btn {
      .btnReset();
      font-size: 1.8rem;
      color: @color-primary;
      position: relative; /* 添加定位 */
      z-index: 1100; /* 提高到1100，超过菜单层级 */
    }
  }

  .mobile-menu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: @color-bg-main;
    padding: 4rem 2rem;
    z-index: 1000;
    overflow-y: auto;

    .mobile-menu-header {
      display: flex;
      justify-content: flex-end;
      padding: 1rem;
      position: absolute;
      top: 0;
      right: 0;
      z-index: 10;
    }

    .mobile-close-btn {
      .btnReset();
      font-size: 1.8rem;
      color: @color-primary;
      z-index: 1002;
    }

    ul {
      margin-top: 3rem; /* 为关闭按钮留出空间 */
      display: flex;
      flex-direction: column;
      gap: 2rem;

      li {
        font-size: 1.2rem;
        color: @color-text-content;
        cursor: pointer;
        .transition(color, 0.3s);
        padding: 0.8rem 1rem;
        border-radius: @radius-base;
        background-color: @color-bg-light;

        &:hover {
          color: @color-tips;
          background-color: @color-bg-light;
        }

        &:active {
          background-color: @color-bg-gray;
        }

        &.active {
          color: @color-active;
          font-weight: bold;
          background-color: @color-status-info;
        }
      }
    }
  }

  .submenu-overlay {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: @color-bg-main;
    border-top: 1px solid @color-bg-light;
    z-index: 99;
    padding: 1rem 2rem;
    box-shadow: 0 4px 6px fade(@color-text-title, 10%);
    transition: opacity 0.3s ease;
  }

  .submenu-content {
    max-width: 100%;
    overflow-x: auto;

    .submenu-sections {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
    }

    .submenu-section {
      flex: 0 0 auto;
      min-width: 220px;
      padding: 0.5rem;

      h4 {
        position: relative;
        cursor: pointer;
        margin-bottom: 0.75rem;
        padding-bottom: 0.25rem;
        display: inline-block;
        .transition(color, 0.3s);

        &:hover {
          color: darken(@color-status-warn, 20%);

          &::after {
            transform: scaleX(1);
          }
        }

        &::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 2px;
          background-color: @color-active;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
      }

      ul {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem 1rem;

        li {
          position: relative;
          cursor: pointer;
          padding-bottom: 0.1rem;
          .transition(color, 0.3s);

          &:hover {
            color: darken(@color-status-warn, 20%);

            &::after {
              transform: scaleX(1);
            }
          }

          &::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 1px;
            background-color: @color-active;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease;
          }
        }
      }
    }
  }

  .mobile-only {
    display: none;
    .mobile-only({
      display: block;
    });
  }

  .desktop-only {
    .mobile-only({
      display: none;
    });
  }

  /* 页面加载动画 */
  .transition-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 2000;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;

    &.active {
      opacity: 1;
    }

    .loader {
      width: 48px;
      height: 48px;
      border: 5px solid @color-primary;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
